import { useTicketContext } from '@Context/TicketContext';
import {
  SquaresFour,
  ArrowSquareOut,
  ArrowClockwise,
} from '@phosphor-icons/react';
import DOMPurify from 'dompurify';
import { JIRA_SANITIZE_OPTIONS } from '@Lib/sanitize';
import JiraAttachmentList from '@Common/JiraAttachmentList';
import { MoonLoader } from 'react-spinners';

const TicketDetails = () => {
  const { selectedTicket, selectTicket, isLoadingDetail, detailError } =
    useTicketContext();

  return (
    <div className="flex h-full w-full flex-col rounded-lg bg-white">
      {/* NO TICKET SELECTED */}
      {!selectedTicket && (
        <span className="flex h-full flex-col items-center justify-center text-neutral-300">
          <SquaresFour size={100} weight="bold" />
          <label className="text-sm text-neutral-500">
            Select a ticket first
          </label>
        </span>
      )}

      {selectedTicket && (
        <div className="relative flex h-full w-full flex-col gap-y-3 p-4">
          {/* Loading overlay */}
          {isLoadingDetail && (
            <div className="absolute inset-0 z-50 flex flex-col items-center justify-center rounded-lg bg-white/80 backdrop-blur-[1px]">
              <MoonLoader size={80} color="#071D49" />
              <span className="mt-4 text-sm font-semibold text-gray-600">
                Loading details...
              </span>
            </div>
          )}

          {/* Error state */}
          {detailError && !isLoadingDetail && (
            <div className="flex flex-col items-center justify-center gap-y-3 py-6">
              <p className="text-sm text-red-600">{detailError}</p>
              <button
                onClick={() => selectTicket(selectedTicket.id)}
                className="inline-flex items-center gap-x-1 rounded-md bg-primary-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-600"
              >
                <ArrowClockwise size={14} weight="bold" />
                Retry
              </button>
            </div>
          )}

          <div className="flex flex-row items-center justify-start gap-x-1">
            <div className="rounded-md bg-primary-700 px-2 py-1 text-xs text-white">
              {selectedTicket.reference_num}
            </div>
            <div
              className="rounded-md bg-primary-700 px-2 py-1 text-xs text-white"
              style={{
                backgroundColor:
                  selectedTicket?.workflow_status?.color ?? '#000000',
              }}
            >
              {selectedTicket.workflow_status?.name ?? 'Status Unknown'}
            </div>
            <div className="font-semibold text-gray-700">
              Type: {selectedTicket.record_type}
            </div>
          </div>

          <div className="border border-gray-200"></div>

          <div className="text-base font-bold text-primary-700 sm:text-2xl md:text-3xl">
            {selectedTicket.name}
          </div>

          <div className="border border-gray-200"></div>

          <div className="flex h-full w-full flex-col gap-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-gray-700">
                Ticket Description
              </label>
              {selectedTicket.url && (
                <a
                  href={selectedTicket.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open Ticket in Jira"
                >
                  <ArrowSquareOut
                    size={20}
                    weight="bold"
                    className="text-primary-700"
                  />
                </a>
              )}
            </div>
            <div className="custom-scroll flex h-0 flex-grow flex-col overflow-y-auto text-sm md:text-base">
              {selectedTicket.isDetailLoaded ? (
                <>
                  {'description' in selectedTicket ? (
                    <div
                      className="adf-body"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          selectedTicket.description || '',
                          JIRA_SANITIZE_OPTIONS
                        ),
                      }}
                    />
                  ) : (
                    <p className="text-gray-500">No description available.</p>
                  )}
                  <JiraAttachmentList
                    headingClassName="text-sm"
                    iconSize={15}
                    attachments={selectedTicket.attachment}
                  />
                </>
              ) : (
                !isLoadingDetail &&
                !detailError && (
                  <p className="text-sm text-gray-400">
                    Click to load ticket details.
                  </p>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetails;
